import { useCallback, useState } from 'react';
import moment from 'moment-timezone';
import qs from 'qs';
import { isEqual, cloneDeep, orderBy } from 'lodash';
import { produce } from 'immer';
import { Location } from 'react-router-dom';

import { AppKey } from '../../libs/apps';
import { PostJSON } from '../../libs/request';
import {
  IPatient,
  IsPatientSnoozed,
  IsPatientArchived,
  IsPatientPaused
} from '../../libs/patient';
import { IAlert, AlertDataValue } from '../../libs/alert';
import { IContact } from '../../libs/contact';
import { IPatientGroup } from '../../libs/group';
import { GetCurrentTZ } from '../../libs/time';
import { addSearchParam } from '../../libs/url';
import useRequestJSON, { RequestResult } from '../../libs/hooks/useRequestJSON';
import useDeepCompareEffect from '../../libs/hooks/useDeepCompareEffect';
import usePrevious from '../../libs/hooks/usePrevious';

import {
  PatientCategory,
  MutexCategories,
  DualCategories,
  TriageResponse,
  TriageData,
  PatientGroupParams,
  TriageParams,
  TriageParamsOptions,
  Page,
  BasicPageOptions,
  PatientPageOptions,
  PaginatedResults,
  Triage,
  TriagedPatient,
  TriagePatientResponse,
  TriagePatientParams,
  TriageCategory,
  CARDINAL_CATEGORIES,
  TriagedApp,
  TriageLoadingState,
  TriageLoadingKey,
  OrderType
} from './types';
import { GetChatThreadsResponse, IChatThread } from '../../libs/chat';

const DEFAULT_PAGE_LIMIT = 10;

export const DEFAULT_TRIAGE_FILTERS: TriageParamsOptions = {
  patients: {
    danger: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    warning: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    success: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    unresponsive: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    inactive: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    completed: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    snoozed: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    paused: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    },
    recent: {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      order: ['alerts', 'desc']
    }
  },
  alerts: {
    page: 0,
    limit: DEFAULT_PAGE_LIMIT,
    unreadOnly: true,
    order: 'desc'
  },
  snoozedAlerts: {
    page: 0,
    limit: DEFAULT_PAGE_LIMIT,
    order: 'desc'
  },
  staleAlerts: {
    page: 0,
    limit: DEFAULT_PAGE_LIMIT
  },
  patientIds: {}
};

type Entity = IAlert | TriagedPatient;

export function getCategoryKey(category: TriageCategory): PatientCategory {
  let key = TriageCategory[category];
  if (key) {
    key = key.toLowerCase();
  }
  return key as PatientCategory;
}

/**
 * Check if patient ids that user is viewing
 * are a part of the initially fetched chats
 *
 * @param patientIds array of patient ids
 */
export const determineMissingChatThreads = (
  patientIds: number[],
  threads: IChatThread[]
): number[] => {
  const patientIdsFromThreads = threads?.map((thread) => thread?.patientId);
  return patientIds.filter((id) => !patientIdsFromThreads.includes(id));
};

// Track patient IDs with in-flight requests to prevent duplicate API calls
const inFlightPatientIds = new Set<number>();

export const getAndAddMissingThreads = (
  patientIds: number[],
  addThreadToContext: (threads: IChatThread[]) => void
): void => {
  const newPatientIds = patientIds.filter((id) => !inFlightPatientIds.has(id));
  if (newPatientIds.length > 0) {
    newPatientIds.forEach((id) => inFlightPatientIds.add(id));
    PostJSON({
      url: '/chat/data_threads/',
      params: { filters: { patientIds: newPatientIds } }
    })
      .then((res: GetChatThreadsResponse | any) => {
        const status =
          res && typeof res === 'object' && 'status' in res
            ? (res as any).status
            : undefined;
        const threads =
          res && typeof res === 'object' && Array.isArray((res as any).threads)
            ? (res as GetChatThreadsResponse).threads
            : undefined;

        if (status !== 'OK' || !threads) {
          return;
        }

        if (threads.length > 0) {
          addThreadToContext(threads);
        }
      })
      .catch(() => {
        // Swallow errors; cleanup is handled in finally.
      })
      .finally(() => {
        newPatientIds.forEach((id) => inFlightPatientIds.delete(id));
      });
  }
};

/**
 * Used to narrow down the entity type
 * so that TypeScript plays nice
 *
 * @param entity is an alert or triaged patient
 */
function isAlert(entity: Entity): entity is IAlert {
  return (entity as IAlert).id != null;
}

/**
 * Extract the primary ID of the entity
 *
 * @param entity the entity of interest (triage entries or alerts)
 */
const getId = (entity: Entity): number =>
  isAlert(entity) ? entity.id : entity.patient.id;

/**
 * Create the pagination cursor from the result
 *
 * @param entity the entity of interest (triage entries or alerts)
 */
function extractCursorAndIds(entity: PaginatedResults<Entity>) {
  // Handle empty results
  if (!entity.results.length) {
    return { cursor: null, entityIds: [] };
  }
  // Track the start & end IDs of the result set
  return {
    cursor: {
      startId: getId(entity.results[0]),
      endId: getId(entity.results[entity.results.length - 1])
    },
    entityIds: entity.results.map((entity: Entity) => getId(entity))
  };
}

/**
 * Create the pagination meta data from the result set
 *
 * @param entity the entity result set to create the pagination
 */
function createPage(
  entity: PaginatedResults<Entity>,
  options?: BasicPageOptions
): Page {
  const { cursor, entityIds } = extractCursorAndIds(entity);
  const page: Page = {
    page: options?.page || 0,
    limit: entity.limit,
    total: entity.total,
    cursor,
    entityIds
  };
  return page;
}

/**
 * Return a function to allow adding a single alert to the triage data set properly
 *
 * @param draft the triage data set to add alert for
 */
function addAlert(draft: TriageData) {
  return (alert: IAlert) => {
    // Only when the alert is not already there
    if (!draft.alerts[alert.id]) {
      if (!draft.patients[alert.patientId]) {
        draft.patients[alert.patientId] = alert.patient;
      }
      draft.alerts[alert.id] = alert;
    }
  };
}

/**
 * Check whether to update a triage entry
 *
 * @param a the existing triage entry
 * @param b the updated triaged patient result
 * @returns whether to update
 */
export const ShouldUpdateTriage = (a: Triage, b: TriagedPatient): boolean => {
  const aKeys = Object.keys(a.apps);
  const bKeys = Object.keys(b.apps);
  if (aKeys.length !== bKeys.length) {
    return true;
  }
  return !isEqual(aKeys.sort(), bKeys.sort());
};

/**
 * Return a function to allow adding a single triage entry to the triage data set properly
 *
 * @param draft the triage data set to add entry for
 */
function addTriage(
  draft: TriageData,
  patientId: number,
  result: TriagedPatient
) {
  const alertIds = result.alerts.map((alert) => alert.id);
  // Update the entry if already exists
  if (draft.triages[patientId]) {
    // When the primary category is a cardinal category, update it
    if (CARDINAL_CATEGORIES.includes(result.category)) {
      draft.triages[patientId].primaryCategoryId = result.category;
      // Otherwise, add to the secondary categories
    } else {
      draft.triages[patientId].secondaryCategoryIds.push(result.category);
    }
    // Update the rest of the data
    draft.triages[patientId].alertIds = alertIds;
    for (const key of Object.keys(result.apps)) {
      draft.triages[patientId].apps[key] = result.apps[key];
    }
    // Otherwise, add a new entry
  } else {
    draft.triages[patientId] = {
      primaryCategoryId: result.category,
      secondaryCategoryIds: [],
      patientId,
      alertIds,
      apps: result.apps
    };
  }
  // Extract the alerts
  if (result.alerts) {
    result.alerts.forEach(addAlert(draft));
  }
}

/**
 * Add a patient data object to the triage set
 *
 * @param draft the triage data set to add patient for
 * @param patient the patient to add
 */
function addPatient(draft: TriageData, patient: IPatient) {
  draft.patients[patient.id] = patient;
}

/**
 * Add multiple alerts to the triage data set
 *
 * @param triageData the triage data set
 * @param alerts the array of alerts to add to the data set
 */
function extractAlerts(
  triageData: TriageData,
  alerts: TriageResponse['alerts'],
  options?: TriageParamsOptions
): TriageData {
  return produce(triageData, (draft: TriageData) => {
    if (!draft.pages.alerts) {
      let _options: BasicPageOptions = null;
      if (options?.alerts) {
        _options = options.alerts;
      }
      draft.pages.alerts = createPage(alerts, _options);
    }
    alerts.results.forEach(addAlert(draft));
  });
}

/**
 * Add multiple snoozed alerts to the triage data set
 *
 * @param triageData the triage data set
 * @param alerts the array of snoozed alerts to add to the data set
 */
function extractSnoozedAlerts(
  triageData: TriageData,
  snoozedAlerts: TriageResponse['snoozedAlerts'],
  options?: TriageParamsOptions
): TriageData {
  return produce(triageData, (draft: TriageData) => {
    if (!draft.pages.snoozedAlerts) {
      let _options: BasicPageOptions = null;
      if (options?.snoozedAlerts) {
        _options = options.snoozedAlerts;
      }
      draft.pages.snoozedAlerts = createPage(snoozedAlerts, _options);
      // (S)oon (t)o (un)-snoozed alerts
      draft.pages.snoozedAlerts.stunTotal = snoozedAlerts.stunTotal
        ? snoozedAlerts.stunTotal
        : 0;
    }
    snoozedAlerts.results.forEach(addAlert(draft));
  });
}

/**
 * Add contacts to the triage data set
 *
 * @param triageData the triage data set
 * @param contacts the array of contacts to add to the data set
 */
export function extractContacts(
  triageData: TriageData,
  contacts: TriageResponse['contacts']
): TriageData {
  return produce(triageData, (draft: TriageData) => {
    for (const contact of contacts) {
      draft.contacts[contact.id] = contact;
    }
  });
}

/**
 * Add the bucketed triage entries into the triage data set
 *
 * @param triageData the triage data set
 * @param patients a triage patients response, consisted of multiple categories
 * @param options the request options, used to construct accurate pagination, optional
 */
function extractMultiple(
  triageData: TriageData,
  patients: TriageResponse['patients'],
  options?: TriageParamsOptions
): TriageData {
  return produce(triageData, (draft: TriageData) => {
    // Go through the mutex categories first to ensure primary association
    const categoryNames = MutexCategories.concat(DualCategories);
    for (const categoryName of categoryNames) {
      const category: PaginatedResults<TriagedPatient> = patients[categoryName];
      if (category) {
        let _options: PatientPageOptions = null;
        if (options?.patients && options.patients[categoryName]) {
          _options = options.patients[categoryName];
        }
        draft.patientIdsPerCategory[categoryName] =
          patients[categoryName].patientIds;
        draft.pages[categoryName] = createPage(category, _options);
        category.results.forEach((result) => {
          addPatient(draft, result.patient);
          addTriage(draft, result.patient.id, result);
          result.alerts.forEach(addAlert(draft));
        });
      }
    }
  });
}

/**
 * Add a single triage entry to the triage context
 *
 * @param triageData the triage data set
 * @param data the single triage entry
 */
export function addTriageEntry(
  triageData: TriageData,
  data: TriagedPatient
): TriageData {
  return produce(triageData, (draft: TriageData) => {
    addPatient(draft, data.patient);
    addTriage(draft, data.patient.id, data);
  });
}

/**
 * Update a single triage entry
 *
 * @param draft the triage data set draft
 * @param data the single triage entry
 */
export function updateTriageEntry(
  draft: TriageData,
  data: TriagedPatient
): void {
  const patientId = data.patient.id;
  for (const key of Object.keys(data.apps)) {
    if (draft.triages[patientId].apps[key]) {
      // Update the alert count
      draft.triages[patientId].apps[key].alert = (
        data.apps[key] as TriagedApp
      ).alert;
      // Update the category
      draft.triages[patientId].apps[key].category = (
        data.apps[key] as TriagedApp
      ).category;
    }
  }
  // When the category changes, rectify the count from original category
  if (draft.triages[patientId].primaryCategoryId !== data.category) {
    const fromCategory = getCategoryKey(
      draft.triages[patientId].primaryCategoryId
    );
    if (draft.pages[fromCategory]) {
      if (draft.pages[fromCategory].entityIds.includes(patientId)) {
        draft.pages[fromCategory].entityIds = draft.pages[
          fromCategory
        ].entityIds.filter((id) => id !== patientId);
      }
      draft.pages[fromCategory].total = draft.pages[fromCategory].total - 1;
    }
    const toCategory = getCategoryKey(data.category);
    if (draft.pages[toCategory]) {
      if (!draft.pages[toCategory].entityIds.includes(patientId)) {
        if (ShouldAddToPaginatedResult(draft.pages[toCategory])) {
          draft.pages[toCategory].entityIds.push(patientId);
        }
      }
      draft.pages[toCategory].total = draft.pages[toCategory].total + 1;
    }
  }
  // Set the category
  draft.triages[patientId].primaryCategoryId = data.category;
}

/**
 * Return the references of alerts associated with the patient among the triage data set
 *
 * @param triageData the triage data set
 * @param patientId the patient to find alerts for
 */
function getAlertsForPatient(
  triageData: TriageData,
  patientId: number
): IAlert[] {
  const alerts: IAlert[] = [];
  for (let id of Object.keys(triageData.alerts)) {
    const alert = triageData.alerts[id];
    if (alert.patientId === patientId) {
      alerts.push(alert);
    }
  }
  return alerts;
}

/**
 * Update stale alerts to the triage context
 *
 * @param draft the triage data set draft
 * @param alerts the alerts to add
 */
export function updateStaleAlerts(
  triageData: TriageData,
  staleAlertsData: AlertDataValue
): TriageData {
  return produce(triageData, (draft: TriageData) => {
    const alerts = staleAlertsData.alerts;
    alerts.forEach(addAlert(draft));
  });
}

export function getStaleAlertsData(
  response: AlertDataValue,
  page: number
): Page {
  if (!response) {
    return null;
  }
  let cursor = null;
  let entityIds = [];
  const alerts = response.alerts;
  if (alerts.length > 0) {
    cursor = {
      startId: getId(alerts[0]),
      endId: getId(alerts[alerts.length - 1])
    };
    entityIds = alerts.map((alert) => getId(alert));
  }
  const data = {
    page,
    limit: DEFAULT_PAGE_LIMIT,
    total: response.total,
    cursor,
    entityIds
  };
  return data;
}

/**
 * Return the an object with references to the relevant data among the triage context
 * for a patient
 *
 * @param triageData the triage data set
 * @param patientId the patient to find entry for
 */
export function getTriageEntry(
  triageData: TriageData,
  patientId: number
): TriagedPatient {
  if (!triageData || !patientId) {
    return null;
  }
  // Locate the patient object itself
  const patient = triageData.patients[patientId];
  if (!patient) {
    return null;
  }
  // Locate the triage entry for the patient
  const triage = triageData.triages[patientId];
  if (!triage) {
    return null;
  }
  return {
    patient: patient,
    alerts: getAlertsForPatient(triageData, patientId),
    category: triage.primaryCategoryId,
    apps: triage.apps
  };
}

/**
 * Takes the /triage/data/ response and transforms it into
 * the form we want to use in our React state
 *
 * @param response the response returned by calling /triage/data/
 */
export function createTriageData(
  response?: TriageResponse,
  options?: TriageParamsOptions
): TriageData {
  let triageData: TriageData = {
    pages: {},
    triages: {},
    patients: {},
    alerts: {},
    contacts: {},
    patientIds: response ? response.patientIds : [],
    totalPatients: response ? response.total : 0,
    triageInProgress: response ? !!response.triageInProgress : false,
    patientIdsPerCategory: {}
  };
  if (response) {
    // Assemble the alerts
    triageData = extractAlerts(triageData, response.alerts, options);
    triageData = extractSnoozedAlerts(
      triageData,
      response.snoozedAlerts,
      options
    );
    // Assemble the triage entries
    triageData = extractMultiple(triageData, response.patients, options);
    // Extract the contacts
    triageData = extractContacts(triageData, response.contacts);
  }
  return triageData;
}

export function ShouldAddToPaginatedResult(result: Page): boolean {
  if (!result) {
    return false;
  }
  const { page, limit, total } = result;
  const pages = Math.ceil(total / limit);
  if (pages !== 0 && page !== pages - 1) {
    return false;
  }
  return result.entityIds.length < limit;
}

// Categories

export const IsPatientCanceled = (apps: TriagedPatient['apps']): boolean => {
  let isCanceled = false;
  if (apps && Object.keys(apps).length > 0) {
    isCanceled = true;
  }
  for (const appKey of Object.keys(apps)) {
    const entry: TriagedApp = apps[appKey];
    const schedule = entry.schedule;
    if (schedule && !schedule.isCanceled) {
      isCanceled = false;
      break;
    }
  }
  return isCanceled;
};

export const IsPatientCompleted = (apps: TriagedPatient['apps']): boolean => {
  let isCompleted = false;
  if (apps && Object.keys(apps).length > 0) {
    isCompleted = true;
  }
  for (const appKey of Object.keys(apps)) {
    const entry: TriagedApp = apps[appKey];
    const schedule = entry.schedule;
    if (schedule) {
      const end = schedule.endDate || schedule.end;
      if (!end || moment(end) > moment()) {
        isCompleted = false;
        break;
      }
    }
  }
  return isCompleted;
};

export const GetSecondaryCategoriesForPatient = (
  patient: IPatient,
  apps?: TriagedPatient['apps']
): TriageCategory[] => {
  const categories: TriageCategory[] = [];
  if (IsPatientSnoozed(patient)) {
    categories.push(TriageCategory.Snoozed);
  }
  if (IsPatientPaused(patient)) {
    categories.push(TriageCategory.Paused);
  }
  if (IsPatientArchived(patient)) {
    categories.push(TriageCategory.Archived);
  }
  if (apps && IsPatientCompleted(apps)) {
    categories.push(TriageCategory.Completed);
  }
  if (apps && IsPatientCanceled(apps)) {
    categories.push(TriageCategory.Canceled);
  }
  return categories;
};

export function GetSecondaryCategoryForApp(
  app: TriagedApp,
  category: TriageCategory
): TriageCategory {
  const schedule = app.schedule;
  if (schedule) {
    if (schedule.isCanceled) {
      category = TriageCategory.Canceled;
    } else {
      const end = schedule.end || schedule.endDate;
      if (end && moment(end) < moment()) {
        category = TriageCategory.Completed;
      }
    }
  }
  if (app.waiver) {
    if (!app.waiver.isConfirmed || app.waiver.isRevoked) {
      category = TriageCategory.Inactive;
    }
  }
  if (category === TriageCategory.Inactive) {
    if (app.waiver?.isRevoked) {
      category = TriageCategory.Revoked;
    }
  }
  return category;
}

export const updateSnoozedAlerts = (
  draft: TriageData,
  alerts: IAlert[],
  totalAlerts: number,
  totalSnoozedAlerts: number,
  alertOrder: OrderType,
  hasUnsnoozed: boolean,
  stunAlertIds = []
): {
  newTotalSnoozedAlerts: number | null;
  newTotalAlerts: number | null;
} => {
  let newTotalSnoozedAlerts = null;
  let newTotalAlerts = null;
  for (const alert of alerts) {
    // Update the alert object
    draft.alerts[alert.id] = alert;
    // Update the snoozed alerts page
    const stunThreshold = moment.tz().add(24, 'hours');
    // Stun = soon to be unsnoozed
    let isStunAlert = false;
    /**
     * An unsnoozed alert won't have alert.snoozedUntil
     * any longer. In that case we need to use the
     * stunAlertIds argument to figure out if this
     * alert is a stun alert
     */
    if (alert.snoozedUntil && moment(alert.snoozedUntil) < stunThreshold) {
      isStunAlert = true;
    } else if (stunAlertIds && stunAlertIds.length) {
      if (stunAlertIds.includes(alert.id)) {
        isStunAlert = true;
      }
    }
    const isAlertInSnoozedAlerts =
      draft.pages?.snoozedAlerts &&
      draft.pages.snoozedAlerts.entityIds.includes(alert.id);
    if (isAlertInSnoozedAlerts) {
      // Remove from page if there (indicating we're unsnoozing)
      draft.pages.snoozedAlerts.entityIds =
        draft.pages.snoozedAlerts.entityIds.filter((id) => id !== alert.id);
      // Decrease stunTotal if appropriate
      if (isStunAlert && draft.pages.snoozedAlerts.stunTotal > 0) {
        draft.pages.snoozedAlerts.stunTotal =
          draft.pages.snoozedAlerts.stunTotal - 1;
      }
    } else {
      // Add to the snoozed alerts if appropriate
      if (ShouldAddToPaginatedResult(draft.pages.snoozedAlerts)) {
        draft.pages.snoozedAlerts.entityIds.push(alert.id);

        // Increase stunTotal if appropriate
        if (isStunAlert) {
          draft.pages.snoozedAlerts.stunTotal =
            draft.pages.snoozedAlerts.stunTotal + 1;
        }
      }
    }
    // Resolve the active alerts if needed
    if (draft.pages?.alerts?.entityIds.includes(alert.id)) {
      draft.pages.alerts.entityIds = draft.pages.alerts.entityIds.filter(
        (id) => id !== alert.id
      );
    } else {
      draft.pages.alerts.entityIds.push(alert.id);
      // Properly order the alerts
      const alertsOnPage = draft.pages.alerts.entityIds.map(
        (id) => draft.alerts[id]
      );
      const sorted = orderBy(alertsOnPage, ['createdAt'], [alertOrder]);
      draft.pages.alerts.entityIds = sorted.map((alert) => alert.id);
    }
    // Update the counts only if it's freshly snoozed
    if (
      alert._justSnoozed &&
      draft.pages?.snoozedAlerts &&
      draft.pages?.alerts
    ) {
      draft.pages.snoozedAlerts.total = draft.pages.snoozedAlerts.total + 1;
      newTotalSnoozedAlerts = produce(
        totalSnoozedAlerts,
        (totalSnoozedAlerts) => {
          return totalSnoozedAlerts + 1;
        }
      );
      draft.pages.alerts.total =
        draft.pages.alerts.total === 0 ? 0 : draft.pages.alerts.total - 1;
      newTotalAlerts = produce(totalAlerts, (totalAlerts) => {
        return totalAlerts === 0 ? 0 : totalAlerts - 1;
      });
      // Reverse the count when unsnoozed
    } else if (hasUnsnoozed) {
      draft.pages.snoozedAlerts.total =
        draft.pages.snoozedAlerts.total === 0
          ? 0
          : draft.pages.snoozedAlerts.total - 1;
      newTotalSnoozedAlerts = produce(
        totalSnoozedAlerts,
        (totalSnoozedAlerts) => {
          return totalSnoozedAlerts === 0 ? 0 : totalSnoozedAlerts - 1;
        }
      );
      draft.pages.alerts.total = draft.pages.alerts.total + 1;
      newTotalAlerts = produce(totalAlerts, (totalAlerts) => {
        return totalAlerts - 1;
      });
    }
  }
  return {
    newTotalSnoozedAlerts,
    newTotalAlerts
  };
};

// Requests

const getFromGroupFilters = (
  group: IPatientGroup,
  key: string
): Array<number | string> => {
  if (!group || !group.filters) {
    return [];
  }
  if (group.filters[key]) {
    return group.filters[key];
  }
  return null;
};

export const getPatientGroupParams = (
  group: IPatientGroup
): PatientGroupParams => {
  return {
    apps: getFromGroupFilters(group, 'apps') as AppKey[],
    contactIds: getFromGroupFilters(group, 'contactIds') as number[],
    tagIds: getFromGroupFilters(group, 'tagIds') as number[]
  };
};

const getLoadingState = (
  options: TriageParamsOptions,
  previousOptions: TriageParamsOptions
): TriageLoadingState => {
  const state: Record<string, boolean> = {};
  if (options && previousOptions) {
    for (const key of Object.keys(options.patients)) {
      if (options.patients[key].page !== previousOptions.patients[key].page) {
        state[key] = true;
      } else {
        state[key] = false;
      }
    }
    for (const key of ['alerts', 'snoozedAlerts']) {
      if (options[key].page !== previousOptions[key].page) {
        state[key] = true;
      } else {
        state[key] = false;
      }
    }
  }
  return state;
};

export const getLoadingCategories = (
  state: TriageLoadingState
): TriageLoadingKey[] => {
  const categories: TriageLoadingKey[] = [];
  for (const category of Object.keys(state)) {
    if (state[category]) {
      categories.push(category as TriageLoadingKey);
    }
  }
  return categories;
};

interface TriageDataResult extends RequestResult<TriageData> {
  loadingState?: TriageLoadingState;
  refetch?: () => void;
}

const transform = (options: TriageParamsOptions) => {
  return (response: TriageResponse): TriageData => {
    return createTriageData(response, options);
  };
};

const isPGChanged = (a: IPatientGroup, b: IPatientGroup): boolean => {
  // Loading state does not count as change
  if (a === undefined || b === undefined) {
    return false;
  }
  // If either one is all patients (null) but not the other
  const isANull = a == null;
  const isBNull = b == null;
  if (isANull !== isBNull) {
    return true;
  }
  // Otherwise, if both are null, it's not a change
  if (isANull || isBNull) {
    return false;
  }
  // Otherwise, check the ID of the groups
  return a.id !== b.id;
};

type TriageRequestOptions = TriageParamsOptions & {
  patientId?: number;
  patientTab?: string;
  staleAlerts?: BasicPageOptions;
};

export const useTriageData = (
  activeGroup: IPatientGroup,
  prevActiveGroup: IPatientGroup,
  options: TriageParamsOptions,
  prevOptions: TriageParamsOptions,
  isDisabled: boolean,
  refreshTick = 0
): TriageDataResult => {
  /**
   * This request should only change if patient group or triage
   * options change
   */
  let _options = cloneDeep(options) as TriageRequestOptions;
  // Remove patientId key so backend triage API accepts options
  if (_options && 'patientId' in _options) {
    delete _options.patientId;
  }
  // When patient group changes, also reset the options directly
  if (isPGChanged(activeGroup, prevActiveGroup)) {
    options = DEFAULT_TRIAGE_FILTERS;
    _options = cloneDeep(DEFAULT_TRIAGE_FILTERS) as TriageRequestOptions;
  }
  // Construct app filters
  const {
    apps: appFilters,
    contactIds,
    tagIds
  } = getPatientGroupParams(activeGroup);
  // If _forceAllApps is set (from a dashboard reset) we intentionally skip injecting any app filters
  let appFilterOptions: AppKey[] | undefined = undefined;
  const isForceAllApps = options._forceAllApps === true;
  if (!isForceAllApps) {
    appFilterOptions = appFilters?.length > 0 ? appFilters : undefined;
    if (options._tempAppFilter) {
      appFilterOptions = [options._tempAppFilter as AppKey];
    }
  } else {
    // Explicitly send empty array to represent ALL apps, preventing backend fallback
    appFilterOptions = [];
  }
  for (const key of Object.keys(_options.patients)) {
    _options.patients[key].apps = appFilterOptions;
  }
  _options.patientIds.apps = appFilterOptions;
  if (_options.alerts) {
    _options.alerts.apps = appFilterOptions;
  }
  if (_options.snoozedAlerts) {
    _options.snoozedAlerts.apps = appFilterOptions;
  }
  // Save the filters
  const _appFilters = usePrevious(appFilters);
  const _contactIds = usePrevious(contactIds);
  const _tagIds = usePrevious(tagIds);
  // Create the differentiated loading state
  const [loadingState, setLoadingState] = useState<TriageLoadingState>({});
  useDeepCompareEffect(() => {
    const isPGLoading =
      !isEqual(appFilters, _appFilters) ||
      !isEqual(contactIds, _contactIds) ||
      !isEqual(tagIds, _tagIds);
    const state = getLoadingState(options, prevOptions);
    state.patientGroup = isPGLoading;
    setLoadingState(state);
  }, [options, appFilters, contactIds, tagIds]);
  // Construct the triage request params
  delete _options._tempAppFilter;
  delete _options._forceAllApps; // never send internal flag to backend
  delete (_options as any).patientId;
  delete (_options as any).patientTab;
  delete (_options as any).staleAlerts;
  const coerceInt = (val: any, fallback: number): number => {
    let v: any = val;
    if (Array.isArray(v)) {
      v = v[0];
    }
    const n = Number(v);
    return Number.isFinite(n) ? Math.floor(n) : fallback;
  };
  if (_options?.alerts) {
    _options.alerts.page = coerceInt(_options.alerts.page, 0);
    _options.alerts.limit = coerceInt(
      _options.alerts.limit,
      DEFAULT_PAGE_LIMIT
    );
  }
  if (_options?.snoozedAlerts) {
    _options.snoozedAlerts.page = coerceInt(_options.snoozedAlerts.page, 0);
    _options.snoozedAlerts.limit = coerceInt(
      _options.snoozedAlerts.limit,
      DEFAULT_PAGE_LIMIT
    );
  }
  if (_options?.patients) {
    for (const key of Object.keys(_options.patients)) {
      const p = _options.patients[key];
      if (p) {
        p.page = coerceInt(p.page, 0);
        p.limit = coerceInt(p.limit, DEFAULT_PAGE_LIMIT);
      }
    }
  }
  const params: TriageParams = {
    start: moment().subtract(2, 'weeks').startOf('day').format(),
    options: _options,
    contactIds,
    tagIds
  };
  const request = useCallback(() => {
    return PostJSON<TriageResponse, TriageParams>({
      url: '/triage/data/',
      params
    }).then((response) => {
      return response;
    });
  }, [activeGroup, options, refreshTick]);
  const req = useRequestJSON<TriageResponse, TriageData>({
    request,
    transformFn: transform(_options),
    enabled: !isDisabled
  });
  return {
    ...req,
    loadingState,
    refetch: () => req.run(request, transform(_options))
  };
};

interface TriagePatientData {
  patient: TriagedPatient;
  contacts: IContact[];
}

const transformPatient = (
  response: TriagePatientResponse
): TriagePatientData => {
  return {
    patient: response.data,
    contacts: response.contacts
  };
};

export const useTriagePatientData = (
  patientId: number,
  includePastPrograms = false
): RequestResult<TriagePatientData> => {
  const request = useCallback(
    () =>
      PostJSON<TriagePatientResponse, TriagePatientParams>({
        url: '/triage/data_patient/',
        params: {
          id: patientId,
          start: moment().subtract(2, 'weeks').startOf('day').format(),
          tz: GetCurrentTZ(),
          includePastPrograms
        }
      }),
    [patientId]
  );
  return useRequestJSON<TriagePatientResponse, TriagePatientData>({
    request,
    transformFn: transformPatient,
    enabled: patientId != null && patientId > 0
  });
};

export const GetTriagePatientData = (
  patientId: number,
  includePastPrograms = false
): Promise<TriagedPatient> => {
  return PostJSON<TriagePatientResponse, TriagePatientParams>({
    url: '/triage/data_patient/',
    params: {
      id: patientId,
      start: moment().subtract(2, 'weeks').startOf('day').format(),
      tz: GetCurrentTZ(),
      includePastPrograms
    }
  }).then((response) => {
    if (response.status !== 'OK') {
      return null;
    }
    return (response as TriagePatientResponse).data;
  });
};

/**
 * This small utility is used instead of lodash's isEmpty
 * because it can be used both on objects AND primitives
 *
 * @param value anything
 */
const isEmpty = (value: any) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
};

// URL Options

export const stringifyOptions = (options: TriageParamsOptions): string => {
  if (!options || typeof options !== 'object') {
    return '';
  }
  // Remove all values from options that are the same as DEFAULT_TRIAGE_FILTERS,
  // to shorten the URL string that's produced
  const opts = produce(options, (draft) => {
    for (const key in draft) {
      const value = draft[key];
      const defaultValue = DEFAULT_TRIAGE_FILTERS[key];
      if (key === '_tempAppFilter') {
        if (isEmpty(value)) {
          delete draft[key];
        }
        continue;
      }
      // If both are empty, remove from options
      if (isEmpty(value) && isEmpty(defaultValue)) {
        delete draft[key];
        continue;
      }
      // If both are the exact same, remove from options
      if (isEqual(value, defaultValue)) {
        delete draft[key];
        continue;
      }
      // Loop through sub object
      if (draft[key] && typeof draft[key] === 'object') {
        for (const subKey in draft[key]) {
          const subValue = draft[key][subKey];
          const defaultSubValue =
            DEFAULT_TRIAGE_FILTERS[key] &&
            typeof DEFAULT_TRIAGE_FILTERS[key] === 'object'
              ? DEFAULT_TRIAGE_FILTERS[key][subKey]
              : undefined;
          // If both are empty, remove from sub options
          if (isEmpty(subValue) && isEmpty(defaultSubValue)) {
            delete draft[key][subKey];
            continue;
          }
          // If both are the exact same, remove from sub options
          if (isEqual(subValue, defaultSubValue)) {
            delete draft[key][subKey];
            continue;
          }
        }
      }
    }
  });
  return qs.stringify(opts);
};

export const addOptionsToURL = (
  location: Location,
  options: TriageParamsOptions
): string => {
  const optionsString = stringifyOptions(options);
  const param = `place=${optionsString}`;
  return addSearchParam(location, param);
};

const mergePlacePrefixedKeys = (parsed: any) => {
  Object.keys(parsed).forEach((key) => {
    if (key.startsWith('place=')) {
      const target = key.slice('place='.length);
      const val: any = parsed[key];
      if (val && typeof val === 'object') {
        const destination: any = (parsed[target] ||= {});
        Object.keys(val).forEach((category) => {
          const srcCategory = val[category];
          const dstCategory = destination[category] || {};
          destination[category] = { ...dstCategory, ...srcCategory };
        });
      }
      delete parsed[key];
    }
  });
};

const whitelistTriageAndPagination = (parsed: any) => {
  Object.keys(parsed).forEach((key) => {
    const isTriageStructure =
      key === 'patients' ||
      key === 'alerts' ||
      key === 'snoozedAlerts' ||
      key === 'staleAlerts' ||
      key === 'patientIds';
    const isPaginationKey =
      key.includes('[page]') ||
      key.includes('[limit]') ||
      key.includes('[order]');
    if (!(isTriageStructure || isPaginationKey)) {
      delete parsed[key];
    }
  });
};

export function parseTriageUrl(search: string): TriageParamsOptions {
  const parsed = qs.parse(search, { ignoreQueryPrefix: true });

  // Build base options from place if present
  let placeString = '';
  if (parsed.place) {
    if (Array.isArray(parsed.place)) {
      placeString = (parsed.place as string[]).sort(
        (a, b) => b.length - a.length
      )[0] as string;
    } else if (typeof parsed.place === 'string') {
      placeString = parsed.place as string;
    }
  }

  let baseOptions = DEFAULT_TRIAGE_FILTERS as TriageParamsOptions;
  if (placeString) {
    baseOptions = parseOptionsString(placeString);
  }

  mergePlacePrefixedKeys(parsed);
  delete (parsed as any).place;
  delete (parsed as any)._tempAppFilter;
  // Strip internal-only flag if it ever appears
  delete (parsed as any)._forceAllApps;
  delete (parsed as any).patientId;
  delete (parsed as any).patientTab;

  // Keep only pagination/triage structure, then stringify remainder
  whitelistTriageAndPagination(parsed);
  const remainder = qs.stringify(parsed, {
    encode: false,
    indices: false,
    arrayFormat: 'repeat'
  });

  if (remainder) {
    const merged = {
      ...baseOptions,
      ...parseOptionsString(remainder)
    } as TriageParamsOptions;
    return merged;
  }

  return baseOptions;
}

const ensurePageOptions = <T extends BasicPageOptions>(params: T): T => {
  // Handle arrays from duplicate query params
  let page = params.page;
  if (Array.isArray(page)) {
    page = page[0];
  }
  const pageNum = Number(page);
  params.page = Number.isFinite(pageNum) ? Math.floor(pageNum) : 0;

  let limit = params.limit;
  if (Array.isArray(limit)) {
    limit = limit[0];
  }
  const limitNum = Number(limit);
  params.limit =
    Number.isFinite(limitNum) && limitNum > 0
      ? Math.floor(limitNum)
      : DEFAULT_PAGE_LIMIT;
  return params;
};

const ensurePatientPageOptions = <T extends PatientPageOptions>(
  params: T
): T => {
  // Handle arrays from duplicate query params
  let page = params.page;
  if (Array.isArray(page)) {
    page = page[0];
  }
  const pageNum = Number(page);
  params.page = Number.isFinite(pageNum) ? Math.floor(pageNum) : 0;
  let limit = params.limit;
  if (Array.isArray(limit)) {
    limit = limit[0];
  }
  const limitNum = Number(limit);
  params.limit =
    Number.isFinite(limitNum) && limitNum > 0
      ? Math.floor(limitNum)
      : DEFAULT_PAGE_LIMIT;
  // Ensure order is always an array with 2 elements
  if (
    params.order == null ||
    !Array.isArray(params.order) ||
    params.order.length < 2
  ) {
    params.order = ['alerts', 'desc'];
  }
  return params;
};

export const parseOptionsString = (
  optionsString: string
): TriageParamsOptions => {
  const parsed = (qs as any).parse(optionsString, {
    decoder: (str: string, defaultDecoder: (s: string) => any) => {
      const n = Number(str);
      return Number.isFinite(n) ? n : defaultDecoder(str);
    },
    filter: (prefix: string, value: any) => {
      // patients[*].order -> always array of strings
      if (/^patients\[[^\]]+\]\[order\]$/.test(prefix)) {
        return Array.isArray(value) ? value.map(String) : [String(value)];
      }
      // alerts/snoozedAlerts.order -> 'asc' | 'desc'
      if (/^(alerts|snoozedAlerts)\[order\]$/.test(prefix)) {
        const v = (
          Array.isArray(value) ? String(value[0]) : String(value)
        ).toLowerCase();
        return v === 'asc' || v === 'desc' ? v : undefined;
      }
      return value;
    }
  });
  /**
   * Because we're using Object.assign here on
   * DEFAULT_TRIAGE_FILTERS we need to use immer so that we
   * don't end up updating the actual DEFAULT_TRIAGE_FILTERS
   * value, which would in turn create some funky state update issues
   * (i.e. triage options would be updated "automatically")
   *
   * Immer allows us to safely update the default filters
   * via Object.assign
   */
  const options = produce(DEFAULT_TRIAGE_FILTERS, (draft) => {
    if (parsed._tempAppFilter != null && parsed._tempAppFilter) {
      draft._tempAppFilter = parsed._tempAppFilter as string;
    }
    const patients = Object.assign(draft.patients, parsed.patients);
    const alerts = Object.assign(draft.alerts, parsed.alerts);
    const staleAlerts = Object.assign(
      {},
      draft.staleAlerts || {},
      parsed.staleAlerts || {}
    );
    // Fill in the gaps
    for (const key of Object.keys(patients)) {
      patients[key] = ensurePatientPageOptions(patients[key]);
    }
    draft = Object.assign(draft, parsed);
    draft.patients = patients;
    draft.alerts = ensurePageOptions(alerts);
    draft.staleAlerts = ensurePageOptions(staleAlerts);
  });
  return options;
};
